/**
 * SMS messages held past retention -- owner decision 2026-10-01. Message text is normally deleted after 24 months;
 * a message that open work still needs (a consent review, a reply-reminder task, an unanswered question) is kept.
 * This report lists every such message older than 24 months, what holds it, and how to clear it. Admins only (the
 * route and the server both enforce it). No message text is shown.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getSmsRetentionHeldReport,
  SMS_HOLD_LABEL,
  type SmsHeldCursor,
} from "../../services/smsRetentionHeld.service";
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const SmsRetentionHeldReport: React.FC = () => {
  // Cursor pages (round-3 audit R3-M2): every held message is reachable. `pages` is the stack of cursors visited.
  const [pages, setPages] = useState<(SmsHeldCursor | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["sms-retention-held-report", cursor],
    queryFn: () => getSmsRetentionHeldReport(cursor),
  });

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/reports"
          className="text-sm text-primary-600 hover:text-primary-800"
        >
          ← Reports
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">
          SMS messages held past retention
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Text messages are deleted after 24 months unless open work still needs
          them. These are older than 24 months and are kept until the work below
          is done.
        </p>
      </div>
      {isLoading && <p className="text-sm text-gray-600">Loading…</p>}
      {error && (
        <p className="text-sm text-red-800">
          {(error as Error).message}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </p>
      )}
      {data && data.total === 0 && (
        <p className="text-sm text-gray-700">
          No messages are held past retention.
        </p>
      )}
      {data && data.total > 0 && (
        <>
          <p className="text-sm text-gray-700">
            {data.total === 1 ? "1 message is" : `${data.total} messages are`}{" "}
            held. Page {pages.length}: {data.shown} shown, oldest first.
          </p>
          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2">Received</th>
                  <th className="px-3 py-2">Age</th>
                  <th className="px-3 py-2">Number</th>
                  <th className="px-3 py-2">Held by</th>
                  <th className="px-3 py-2">Assigned</th>
                  <th className="px-3 py-2">How to clear</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {data.rows.map((r) => (
                  <tr key={r.inbound_id}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmtDate(r.received_at)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.age_days} days
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {r.member_masked}
                    </td>
                    <td className="px-3 py-2">
                      {SMS_HOLD_LABEL[r.hold] ?? r.hold}
                    </td>
                    <td className="px-3 py-2">
                      {r.assigned_agent ??
                        (r.support_task_id ? "Unassigned" : "—")}
                    </td>
                    <td className="px-3 py-2">
                      {r.how_to_clear}
                      {r.conversation_id && (
                        <>
                          {" "}
                          <Link
                            to={`/messages/${r.conversation_id}`}
                            className="text-primary-600 hover:text-primary-800"
                          >
                            Open conversation
                          </Link>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={pages.length === 1}
              onClick={() => setPages((p) => p.slice(0, -1))}
            >
              Previous page
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={!data.next}
              onClick={() => data.next && setPages((p) => [...p, data.next])}
            >
              Next page
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SmsRetentionHeldReport;
